import { connect } from 'react-redux';

import { Combobox, Label } from 'components';
import { useLocale, useTranslations } from 'next-intl';
import { FC } from 'react';
import { Option } from 'types/Option';
import { availableLanguageOptions } from 'utils/availableLanguageOptions';
import { updateInputField } from './_actions';
import { getMostUsedSourceLanguages, getMostUsedTargetLanguages } from './_selectors';

interface ILanguagePairs {
  inputs: {
    sourceLanguageCode: string;
    targetLanguageCodes: string[];
  };
  sourceLanguageCodes: string[];
  targetLanguageCodes: string[];
  mostUsedSourceLanguages: string[];
  mostUsedTargetLanguages: string[];
  excludedLanguageCodes: string[];
  hideTargetLanguages: boolean;
  updateInputField: (payload: { field: string; value: any }) => void;
}

const LanguagePairs: FC<ILanguagePairs> = ({
  inputs,
  sourceLanguageCodes,
  targetLanguageCodes,
  mostUsedSourceLanguages,
  mostUsedTargetLanguages,
  excludedLanguageCodes,
  hideTargetLanguages,
  updateInputField
}) => {
  const t = useTranslations();
  const locale = useLocale();

  // const selectedOptions = visibleGlossaryLanguages.map((lang) => t(`languages.${lang}` as any));

  const onChangeSource = (option: Option) =>
    option?.value && updateInputField({ field: 'sourceLanguageCode', value: option?.value });

  const onChangeTargetEdit = (index: number) => (option: Option) =>
    option?.value &&
    updateInputField({
      field: 'targetLanguageCodes',
      value: [
        ...inputs.targetLanguageCodes.slice(0, index),
        option?.value,
        ...inputs.targetLanguageCodes.slice(index + 1)
      ]
    });

  const onChangeTargetAdd = (option: Option) =>
    option?.value &&
    updateInputField({
      field: 'targetLanguageCodes',
      value: [...inputs.targetLanguageCodes, option?.value]
    });

  return (
    <>
      <Label name={'source'}>
        {hideTargetLanguages
          ? t('ordering_form.language_source')
          : t('ordering_form.language_pair')}
      </Label>
      <div className="ordering_form__split_inputs mb-2">
        <div className="ordering_form__split_input">
          <Combobox
            id="sourceLanguageCode"
            selectedValue={inputs.sourceLanguageCode}
            onChange={onChangeSource}
            noClose
            wideHeight
            placeholder={t('ordering_form.source_language')}
            options={[
              ...availableLanguageOptions(
                sourceLanguageCodes,
                excludedLanguageCodes,
                mostUsedSourceLanguages,
                inputs.sourceLanguageCode,
                t,
                locale
              )
            ]}
          />
        </div>
        {!hideTargetLanguages && (
          <div className="ordering_form__split_input">
            {inputs.targetLanguageCodes?.map((tlc, index) => (
              <div style={{ position: 'relative' }} key={`editTargetLanguageCode_${tlc}_${index}`}>
                <Combobox
                  id={`editTargetLanguageCode_${tlc}_${index}`}
                  selectedValue={tlc}
                  onChange={onChangeTargetEdit(index)}
                  noClose
                  wideHeight
                  placeholder={t('ordering_form.target_language')}
                  options={availableLanguageOptions(
                    targetLanguageCodes,
                    excludedLanguageCodes,
                    mostUsedTargetLanguages,
                    tlc,
                    t,
                    locale
                  )}
                />
                <span
                  className="ticon-close ordering_form__remove_field pt-1"
                  onClick={() =>
                    updateInputField({
                      field: 'targetLanguageCodes',
                      value: inputs.targetLanguageCodes.filter((_, i) => i != index)
                    })
                  }
                />
              </div>
            ))}
            <Combobox
              id="addTargetLanguageCode"
              onChange={onChangeTargetAdd}
              wideHeight
              noClose
              cleanOnSelect
              placeholder={t('ordering_form.target_language')}
              options={availableLanguageOptions(
                targetLanguageCodes,
                excludedLanguageCodes,
                mostUsedTargetLanguages,
                undefined,
                t,
                locale
              )}
              selectedOptions={inputs.targetLanguageCodes}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default connect(
  (state: {
    inputs: { sourceLanguageCode: string; targetLanguageCodes: string[] };
    staticData: { sourceLanguageCodes: string[]; targetLanguageCodes: string[] };
  }) => {
    return {
      inputs: state?.inputs,
      sourceLanguageCodes: state?.staticData?.sourceLanguageCodes,
      excludedLanguageCodes: [
        ...(state?.inputs.targetLanguageCodes ?? []),
        state?.inputs.sourceLanguageCode
      ],
      targetLanguageCodes: state?.staticData?.targetLanguageCodes,
      mostUsedSourceLanguages: getMostUsedSourceLanguages(state),
      mostUsedTargetLanguages: getMostUsedTargetLanguages(state)
    };
  },
  { updateInputField }
)(LanguagePairs);
